import React, {useCallback, useContext, useEffect, useState} from 'react';
import UserContext from '../common/UserContext.js';

// css
import '../../assets/css/FlexibleSpendingAccounts.css';

// components
import {
    ColumnMenu,
    CurrencyCell, TooltipHeaderCell
} from '../common/Grid.js';
import {HamburgerMenuCell} from './HamburgerMenuCell.js';
import {formatCurrency} from '../common/utilities.js';

// kendo react
import {Grid, GridColumn, GridNoRecords} from '@progress/kendo-react-grid';
import {Tooltip} from '@progress/kendo-react-tooltip';
import {DatePickerPropsContext} from '@progress/kendo-react-dateinputs';
import {process} from '@progress/kendo-data-query';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {
    agreementsKey,
    startDateKey,
    endDateKey,
    budgetKey,
    overdraftKey,
    extendedBudgetKey,
    remainingBudgetKey,
    noDataAvailableKey,
    mainMessages, budgetDefKey, extendedBudgetDefKey, remainingBudgetDefKey
} from '../../assets/text/MultilingualText.js';
import {Button} from "@progress/kendo-react-buttons";
import {pencilIcon} from "@progress/kendo-svg-icons";

// consts for fsa grid
const initialGridState = {
    skip: 0,
    take: 20
};

const FlexibleSpendingAccountsGrid = (props) => {
    const {agreements} = props;
    const {siteLanguageDefault} = useContext(UserContext)
    const localization = useLocalization();

    // convert date/string to into KSM date format (e.g. March 9, 2024)
    const convertDate = (date) => {
        if (date === null || date === undefined || date === '' || date.toString() === "Invalid Date") {
            return null;
        } else if (date.toString().toLowerCase() === "expired") {
            let expiredString = date.toLowerCase();
            return expiredString.charAt(0).toUpperCase() + expiredString.slice(1);
        }

        date = new Date(date);

        const options = {month: 'long', day: 'numeric', year: 'numeric'};
        return new Intl.DateTimeFormat('en-US', options).format(date);
    };

    // formatted date cell with non-date col data
    const DateCell = (props) => {
        const field = props.field;
        return (
            <td>
                {convertDate(props.dataItem[field])}
            </td>
        );
    };

    // percent currency cell
    const PercentCurrencyCell = (props) => {
        return (
            <td style={{textAlign: 'right'}}>
                <Tooltip
                    anchorElement="target"
                    showCallout={true}
                    parentTitle={true}
                    openDelay={0}
                    position="top"
                >
                    <div title={props.dataItem.percent}>
                        {formatCurrency(
                            props.dataItem[props.field],
                            props.dataItem['currency_locale'], props.dataItem['currency_code']
                        )}
                    </div>
                </Tooltip>
            </td>
        )
    }

    // apply disabled styling when ea is expired
    const rowRender = (trElement, props) => {
        let expired = props.dataItem.expired;
        const expiredStyle = {
            color: '#AAAAAA',
        };
        const trProps = {
            style: expired ? expiredStyle : {},
        };
        return React.cloneElement(
            trElement,
            {...trProps},
            trElement.props.children
        );
    };

    const [gridState, setGridState] = useState(initialGridState);
    const [dataState, setDataState] = useState(
        process(
            agreements.map((item) => ({...item,})),
            initialGridState)
    );

    const onDataStateChange = useCallback((event) => {
        const newDataState = process(
            agreements.map((item) => ({...item})),
            event.dataState
        );
        setGridState(event.dataState);
        setDataState(newDataState);
    }, [agreements]);

    useEffect(() => {
        const newDataState = process(
            agreements.map((item) => ({...item})),
            gridState
        );
        setDataState(newDataState);
    }, [agreements]); // eslint-disable-line react-hooks/exhaustive-deps

    // initialize fsa table
    useEffect(() => {
        if (agreements.length) {
            setDataState(process(agreements, gridState));
        } else {
            setDataState(process([], gridState));
        }
    }, [agreements]); // eslint-disable-line react-hooks/exhaustive-deps

    const dateFilterFormat = useCallback(
        (calendarProps) => ({
            ...calendarProps,
            format: 'dd-MMM-yyyy',
        }),
        []
    );

    return (
        <DatePickerPropsContext.Provider value={dateFilterFormat}>
            <Grid
                className={'fsa-grid'}
                scrollable={"none"}
                sortable={true}
                pageable={(agreements.length <= 25) ? false : {
                    buttonCount: 5, pageSizes: [10, 20, 50, 100],
                }}
                total={dataState.total}
                data={dataState}
                onDataStateChange={onDataStateChange}
                rowRender={rowRender}
                {...gridState}
            >
                <GridNoRecords>
                    {localization.toLanguageString(noDataAvailableKey, mainMessages[siteLanguageDefault][noDataAvailableKey])}
                </GridNoRecords>
                <GridColumn
                    cell={(props) =>
                        <HamburgerMenuCell
                            localization={localization}
                            siteLanguageDefault={siteLanguageDefault}
                            {...props}
                        />
                    }
                    width={"1.25rem"}
                />
                <GridColumn
                    field="agreement"
                    title={localization.toLanguageString(agreementsKey, mainMessages[siteLanguageDefault][agreementsKey])}
                    columnMenu={ColumnMenu}
                />
                <GridColumn
                    field="start_date"
                    title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                    cell={DateCell}
                    filter={'date'}
                    columnMenu={ColumnMenu}
                    editable={false}
                />
                <GridColumn
                    field="end_date"
                    title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                    cell={DateCell}
                    filter={'date'}
                    columnMenu={ColumnMenu}
                    editable={false}
                />
                <GridColumn
                    field="budget"
                    title={localization.toLanguageString(budgetKey, mainMessages[siteLanguageDefault][budgetKey])}
                    columnMenu={ColumnMenu}
                    cell={CurrencyCell}
                    filter={"numeric"}
                    headerCell={() => <TooltipHeaderCell
                        title={localization.toLanguageString(budgetKey, mainMessages[siteLanguageDefault][budgetKey])}
                        tooltip={localization.toLanguageString(budgetDefKey, mainMessages[siteLanguageDefault][budgetDefKey])}
                    />}
                />
                <GridColumn
                    field="overdraft_budget"
                    title={localization.toLanguageString(overdraftKey, mainMessages[siteLanguageDefault][overdraftKey])}
                    columnMenu={ColumnMenu}
                    cell={PercentCurrencyCell}
                    filter={"numeric"}
                />
                <GridColumn
                    field="total_budget"
                    title={localization.toLanguageString(extendedBudgetKey, mainMessages[siteLanguageDefault][extendedBudgetKey])}
                    columnMenu={ColumnMenu}
                    cell={CurrencyCell}
                    filter={"numeric"}
                    headerCell={() => <TooltipHeaderCell
                        title={localization.toLanguageString(extendedBudgetKey, mainMessages[siteLanguageDefault][extendedBudgetKey])}
                        tooltip={localization.toLanguageString(extendedBudgetDefKey, mainMessages[siteLanguageDefault][extendedBudgetDefKey])}
                    />}
                />
                <GridColumn
                    field="total_remaining_budget"
                    title={localization.toLanguageString(remainingBudgetKey, mainMessages[siteLanguageDefault][remainingBudgetKey])}
                    columnMenu={ColumnMenu}
                    cell={CurrencyCell}
                    filter={"numeric"}
                    headerCell={() => <TooltipHeaderCell
                        title={localization.toLanguageString(remainingBudgetKey, mainMessages[siteLanguageDefault][remainingBudgetKey])}
                        tooltip={localization.toLanguageString(remainingBudgetDefKey, mainMessages[siteLanguageDefault][remainingBudgetDefKey])}
                    />}
                />
            </Grid>
        </DatePickerPropsContext.Provider>
    )
}

export default FlexibleSpendingAccountsGrid;